import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Stack,
  Divider,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import type {
  DebugPanelConfig,
  DebugSectionId,
  DebugDataSection,
  HookStateEntry,
  HookStatus,
  CacheRegistration,
} from '../../types/debugPanel';
import type { DebugNavigationRequest } from '../../hooks/useDebugSectionFocus';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import {
  getApiCallLog,
  clearApiCallLog,
  onApiCall,
  type ApiCallEntry,
} from '../../services/apiCallLogger';
import {
  getSignalRBroadcastLog,
  clearSignalRBroadcastLog,
  onSignalRBroadcast,
  type SignalRBroadcastEntry,
} from '../../services/signalRService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<HookStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  idle: 'default',
  loading: 'info',
  loaded: 'success',
  error: 'error',
  stale: 'warning',
};

function httpStatusColor(status: number | null): 'success' | 'warning' | 'error' | 'default' {
  if (status === null) return 'error';
  if (status >= 200 && status < 300) return 'success';
  if (status >= 400 && status < 500) return 'warning';
  if (status >= 500) return 'error';
  return 'default';
}

function methodColor(method: string): 'primary' | 'success' | 'warning' | 'error' | 'info' {
  switch (method) {
    case 'GET': return 'info';
    case 'POST': return 'success';
    case 'PUT': return 'warning';
    case 'PATCH': return 'warning';
    case 'DELETE': return 'error';
    default: return 'primary';
  }
}

function relativeTime(timestamp: number): string {
  const diff = Math.round((Date.now() - timestamp) / 1000);
  if (diff < 1) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionsTab({
  config,
  focusedSection,
}: {
  config: DebugPanelConfig;
  focusedSection?: DebugSectionId;
}) {
  const [expandedId, setExpandedId] = useState<string | false>(focusedSection || false);

  useEffect(() => {
    if (focusedSection) setExpandedId(focusedSection);
  }, [focusedSection]);

  const sectionData = (sectionId: string): DebugDataSection[] =>
    config.dataSections.filter((ds) => ds.sectionId === sectionId);

  return (
    <>
      {config.sections.map((section) => (
        <Accordion
          key={section.id}
          expanded={expandedId === section.id}
          onChange={(_, isExpanded) => setExpandedId(isExpanded ? section.id : false)}
          disableGutters
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" fontWeight="bold">
              {section.label}
            </Typography>
            <Chip
              label={`${sectionData(section.id).length} groups`}
              size="small"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {sectionData(section.id).length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No data
              </Typography>
            ) : (
              <Stack spacing={2} sx={{ p: 2 }}>
                {sectionData(section.id).map((ds) => (
                  <Box key={ds.label}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      {ds.label}
                    </Typography>
                    {ds.entries.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Empty
                      </Typography>
                    ) : ds.entries[0]?.table ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {ds.entries[0].table.headers.map((h) => (
                                <TableCell key={h}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ds.entries[0].table.rows.map((row, ri) => (
                              <TableRow key={ri}>
                                {row.map((cell, ci) => (
                                  <TableCell key={ci}>{cell}</TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Key</TableCell>
                              <TableCell>Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ds.entries.map((entry) => (
                              <TableRow key={entry.label}>
                                <TableCell>
                                  <code>{entry.label}</code>
                                </TableCell>
                                <TableCell>
                                  {entry.content === null || entry.content === undefined
                                    ? <Typography variant="body2" color="text.secondary">—</Typography>
                                    : typeof entry.content === 'boolean'
                                      ? <Chip label={String(entry.content)} size="small" color={entry.content ? 'success' : 'default'} />
                                      : String(entry.content)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
}

function HooksTab({ hookStates }: { hookStates: HookStateEntry[] }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Hook</TableCell>
            <TableCell>Section</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Records</TableCell>
            <TableCell>Detail / Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {hookStates.map((hook) => (
            <TableRow key={hook.name}>
              <TableCell>
                <code>{hook.name}</code>
              </TableCell>
              <TableCell>{hook.section}</TableCell>
              <TableCell>
                <Chip label={hook.status} size="small" color={STATUS_COLORS[hook.status]} />
              </TableCell>
              <TableCell align="right">
                {hook.recordCount !== undefined ? hook.recordCount : '—'}
              </TableCell>
              <TableCell>
                {hook.error ? (
                  <Typography variant="body2" color="error.main" sx={{ fontSize: '0.75rem' }}>
                    {hook.error}
                  </Typography>
                ) : hook.detail ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {hook.detail}
                  </Typography>
                ) : (
                  '—'
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ApiLogTab() {
  const [entries, setEntries] = useState<ApiCallEntry[]>(getApiCallLog);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onApiCall(() => {
      setEntries(getApiCallLog());
    });
    return unsub;
  }, []);

  const handleClear = () => {
    clearApiCallLog();
    setEntries([]);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {entries.length} call{entries.length !== 1 ? 's' : ''} logged
        </Typography>
        <Button size="small" startIcon={<DeleteOutlineIcon />} onClick={handleClear}>
          Clear Log
        </Button>
      </Box>
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No API calls logged yet
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: '40vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <React.Fragment key={entry.id}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      {relativeTime(entry.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Chip label={entry.method} size="small" color={methodColor(entry.method)} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <code>{entry.endpoint}</code>
                    </TableCell>
                    <TableCell>
                      {entry.status !== null ? (
                        <Chip label={entry.status} size="small" color={httpStatusColor(entry.status)} />
                      ) : (
                        <Chip label={entry.error || 'ERR'} size="small" color="error" />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      {entry.durationMs}ms
                    </TableCell>
                  </TableRow>
                  {expandedId === entry.id && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ bgcolor: 'grey.50' }}>
                        <Stack spacing={1} sx={{ py: 1 }}>
                          {entry.requestBody !== undefined && (
                            <Box>
                              <Typography variant="caption" fontWeight="bold">Request Body:</Typography>
                              <Box component="pre" sx={{ fontSize: '0.7rem', m: 0, mt: 0.5, p: 1, bgcolor: 'grey.100', borderRadius: 1, overflow: 'auto', maxHeight: 200 }}>
                                {typeof entry.requestBody === 'string'
                                  ? entry.requestBody
                                  : JSON.stringify(entry.requestBody, null, 2)}
                              </Box>
                            </Box>
                          )}
                          {entry.responseBody !== undefined && (
                            <Box>
                              <Typography variant="caption" fontWeight="bold">Response Body:</Typography>
                              <Box component="pre" sx={{ fontSize: '0.7rem', m: 0, mt: 0.5, p: 1, bgcolor: 'grey.100', borderRadius: 1, overflow: 'auto', maxHeight: 200 }}>
                                {typeof entry.responseBody === 'string'
                                  ? entry.responseBody
                                  : JSON.stringify(entry.responseBody, null, 2)}
                              </Box>
                            </Box>
                          )}
                          {entry.error && (
                            <Box>
                              <Typography variant="caption" fontWeight="bold" color="error.main">Error:</Typography>
                              <Typography variant="body2" color="error.main">{entry.error}</Typography>
                            </Box>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function CachesTab({ caches }: { caches: CacheRegistration[] }) {
  const [, forceRender] = useState(0);
  const [expandedCache, setExpandedCache] = useState<string | null>(null);
  const [refreshingCache, setRefreshingCache] = useState<string | null>(null);

  const handleClearAll = () => {
    caches.forEach((c) => c.clear?.());
    forceRender((n) => n + 1);
  };

  const handleClear = (cache: CacheRegistration) => {
    cache.clear?.();
    forceRender((n) => n + 1);
  };

  const handleRefresh = async (cache: CacheRegistration) => {
    setRefreshingCache(cache.name);
    try {
      await cache.refresh?.();
    } finally {
      setRefreshingCache(null);
      forceRender((n) => n + 1);
    }
  };

  const toggleShowRecords = (cacheName: string) => {
    setExpandedCache(prev => prev === cacheName ? null : cacheName);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button size="small" startIcon={<DeleteOutlineIcon />} onClick={handleClearAll} color="warning">
          Clear All Caches
        </Button>
      </Box>
      <Stack spacing={1}>
        {caches.map((cache) => (
          <Paper key={cache.name} variant="outlined" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {cache.name}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap">
                  <Chip label={cache.type} size="small" variant="outlined" />
                  <Chip
                    icon={cache.getData ? (expandedCache === cache.name ? <VisibilityOffIcon /> : <VisibilityIcon />) : undefined}
                    label={`${cache.getRecordCount()} records`}
                    size="small"
                    variant="outlined"
                    color={cache.getRecordCount() > 0 ? 'success' : 'default'}
                    onClick={cache.getData ? () => toggleShowRecords(cache.name) : undefined}
                    sx={cache.getData ? { cursor: 'pointer' } : undefined}
                  />
                  {cache.getSizeEstimate && (
                    <Chip label={cache.getSizeEstimate()} size="small" variant="outlined" />
                  )}
                  {cache.getAge && (
                    <Chip
                      label={cache.getAge() ? `Age: ${cache.getAge()}` : 'No cache'}
                      size="small"
                      variant="outlined"
                      color={cache.getAge() ? 'info' : 'default'}
                    />
                  )}
                  <Chip label={cache.section} size="small" />
                </Stack>
              </Box>
              <Stack direction="row" spacing={0.5}>
                {cache.clear ? (
                  <Tooltip title="Clear cache">
                    <IconButton size="small" onClick={() => handleClear(cache)} color="warning">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="No clear method available">
                    <span>
                      <IconButton size="small" disabled>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {cache.refresh ? (
                  <Tooltip title={refreshingCache === cache.name ? 'Refreshing…' : 'Refresh cache'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleRefresh(cache)}
                        color="primary"
                        disabled={refreshingCache !== null}
                      >
                        {refreshingCache === cache.name
                          ? <CircularProgress size={16} color="primary" />
                          : <RefreshIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title="No refresh method available (see TODO)">
                    <span>
                      <IconButton size="small" disabled>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </Box>
            <Collapse in={expandedCache === cache.name}>
              {expandedCache === cache.name && cache.getData && (
                <Box
                  component="pre"
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    fontSize: '0.7rem',
                    overflow: 'auto',
                    maxHeight: 300,
                  }}
                >
                  {JSON.stringify(cache.getData(), null, 2)}
                </Box>
              )}
            </Collapse>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}

function SignalRTab() {
  const [entries, setEntries] = useState<SignalRBroadcastEntry[]>(getSignalRBroadcastLog);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onSignalRBroadcast(() => {
      setEntries(getSignalRBroadcastLog());
    });
    return unsub;
  }, []);

  const handleClear = () => {
    clearSignalRBroadcastLog();
    setEntries([]);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {entries.length} broadcast{entries.length !== 1 ? 's' : ''} logged
        </Typography>
        <Button size="small" startIcon={<DeleteOutlineIcon />} onClick={handleClear}>
          Clear Log
        </Button>
      </Box>
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No SignalR broadcasts logged yet
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: '40vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Data</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <React.Fragment key={entry.id}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      {relativeTime(entry.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Chip label={entry.eventName} size="small" color="info" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      {typeof entry.data === 'string' ? entry.data : JSON.stringify(entry.data)}
                    </TableCell>
                  </TableRow>
                  {expandedId === entry.id && (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ bgcolor: 'grey.50' }}>
                        <Box component="pre" sx={{ fontSize: '0.7rem', m: 0, p: 1, bgcolor: 'grey.100', borderRadius: 1, overflow: 'auto', maxHeight: 200 }}>
                          {JSON.stringify(entry.data, null, 2)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface DebugPanelProps {
  config: DebugPanelConfig;
  focusedSection?: DebugSectionId;
  navigationRequest?: DebugNavigationRequest;
}

const DebugPanelInner: React.FC<DebugPanelProps> = ({ config, focusedSection, navigationRequest }) => {
  const [expanded, setExpanded] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [apiCallCount, setApiCallCount] = useState(getApiCallLog().length);
  const [broadcastCount, setBroadcastCount] = useState(getSignalRBroadcastLog().length);
  const [lastNavKey, setLastNavKey] = useState(0);

  useEffect(() => {
    const unsub = onApiCall(() => {
      setApiCallCount(getApiCallLog().length);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSignalRBroadcast(() => {
      setBroadcastCount(getSignalRBroadcastLog().length);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (navigationRequest && navigationRequest.key > 0 && navigationRequest.key !== lastNavKey) {
      setLastNavKey(navigationRequest.key);
      setExpanded(true);
      setTabIndex(0);
    }
  }, [navigationRequest, lastNavKey]);

  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        borderRadius: '8px 8px 0 0',
      }}
    >
      {/* Toggle bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          bgcolor: 'grey.100',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.200' },
          borderRadius: expanded ? 0 : '8px 8px 0 0',
        }}
        onClick={handleToggle}
        role="button"
        aria-expanded={expanded}
        aria-label="Toggle debug panel"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Debug Panel
          </Typography>
          <Chip
            label={`${config.hookStates.length} hooks`}
            size="small"
            variant="outlined"
            onClick={(e) => { e.stopPropagation(); setExpanded(true); setTabIndex(1); }}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label={`${config.caches.length} caches`}
            size="small"
            variant="outlined"
            onClick={(e) => { e.stopPropagation(); setExpanded(true); setTabIndex(3); }}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label={`${apiCallCount} API calls`}
            size="small"
            variant="outlined"
            onClick={(e) => { e.stopPropagation(); setExpanded(true); setTabIndex(2); }}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label={`${broadcastCount} broadcasts`}
            size="small"
            variant="outlined"
            onClick={(e) => { e.stopPropagation(); setExpanded(true); setTabIndex(4); }}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
        <IconButton size="small" aria-label={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Expandable content */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              variant="fullWidth"
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
            >
              <Tab label="Sections" />
              <Tab label="Hooks" />
              <Tab label="API Log" />
              <Tab label="Caches" />
              <Tab label="SignalR" />
            </Tabs>
          </Box>
          <Box sx={{ p: 2 }}>
            {tabIndex === 0 && (
              <SectionsTab config={config} focusedSection={focusedSection} />
            )}
            {tabIndex === 1 && (
              <HooksTab hookStates={config.hookStates} />
            )}
            {tabIndex === 2 && (
              <ApiLogTab />
            )}
            {tabIndex === 3 && (
              <CachesTab caches={config.caches} />
            )}
            {tabIndex === 4 && (
              <SignalRTab />
            )}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

/**
 * Developer tool — hook/cache/API-call/broadcast inspector. Shown only to a user
 * who has turned on "Show debug panel" on their Settings page
 * (`preferences.showDebugPanel`, default off), in every build. It used to be
 * gated on NODE_ENV alone, after it was found on a phone pinned to the bottom of
 * the live dashboard (2026-10-16); off-by-default keeps that from recurring while
 * letting whoever needs it switch it on in production. Below 900px it also needs
 * "Also show on phones" (`preferences.showDebugPanelOnPhones`, default off): the
 * preference follows the user, so one switch couldn't mean desktop only. The
 * wrapper keeps DebugPanelInner's hooks unconditional.
 */
export const DebugPanel: React.FC<DebugPanelProps> = (props) => {
  const currentUser = useCurrentUser();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const preferences = currentUser?.preferences;
  const visible = preferences?.showDebugPanel && (!isPhone || preferences.showDebugPanelOnPhones);
  return visible ? <DebugPanelInner {...props} /> : null;
};
